import { useState, useMemo, useEffect, useCallback } from 'react';
import { MessageSquare, CheckSquare, Target, ChevronDown, Plus, Edit2, Trash2, AlertTriangle, CheckCircle, Circle, Users, Archive, FileText } from 'lucide-react';
import Section from '../components/Section';
import Checkbox from '../components/Checkbox';
import ConfirmModal from '../components/ConfirmModal';
import TopicModal from '../components/TopicModal';
import ActionItemModal from '../components/ActionItemModal';
import RelationshipCard from '../components/RelationshipCard';
import AddRelationshipModal from '../components/AddRelationshipModal';
import { useOneOnOneRelationships, DAY_NAMES } from '../hooks/useOneOnOneRelationships';
import { useOneOnOnes } from '../hooks/useOneOnOnes';
import { useTopics } from '../hooks/useTopics';
import { useActionItems } from '../hooks/useActionItems';
import { useGoals } from '../hooks/useGoals';
import { useAuth } from '../hooks/useAuth';
import { useOneOnOneNotes } from '../hooks/useOneOnOneNotes';
import FeatureOnboarding from '../components/common/FeatureOnboarding';

function getInternalUserName(displayName) {
  if (!displayName) return null;
  const firstName = displayName.split(' ')[0];
  if (firstName === 'Jason') return 'Jason';
  if (firstName === 'Matt' || firstName === 'Matthew') return 'Matt';
  return firstName;
}

function formatDate(dateStr) {
  if (!dateStr) return '';
  const date = new Date(dateStr + 'T00:00:00');
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export default function OneOnOnePage() {
  const { user, isAdmin } = useAuth();
  const currentUserName = getInternalUserName(user?.displayName);

  // Relationship management
  const {
    activeRelationships,
    archivedRelationships,
    loading: relationshipsLoading,
    addRelationship,
    updateRelationship,
    archiveRelationship,
    restoreRelationship
  } = useOneOnOneRelationships();

  // Selected relationship state
  const [selectedRelationshipId, setSelectedRelationshipId] = useState(null);
  const [showArchived, setShowArchived] = useState(false);

  // Auto-select first relationship
  useEffect(() => {
    if (!selectedRelationshipId && activeRelationships.length > 0) {
      setSelectedRelationshipId(activeRelationships[0].id);
    }
  }, [activeRelationships, selectedRelationshipId]);

  // Get the selected relationship object
  const selectedRelationship = useMemo(() => {
    const allRelationships = [...activeRelationships, ...archivedRelationships];
    return allRelationships.find(r => r.id === selectedRelationshipId) || null;
  }, [selectedRelationshipId, activeRelationships, archivedRelationships]);

  // 1:1 session for the selected relationship
  const {
    sessions,
    currentSession,
    pastSessions,
    formatWeekDisplay,
    completeSession,
    uncompleteSession,
    createFirstSession,
    isSessionEditable,
    loading: sessionsLoading
  } = useOneOnOnes(selectedRelationship);

  // State for creating session
  const [isCreatingSession, setIsCreatingSession] = useState(false);

  // Get all session IDs for this relationship (for carryover filtering)
  const relationshipSessionIds = useMemo(() => sessions.map(s => s.id), [sessions]);

  // Topics and action items for current session
  const { topics, addTopic, updateTopic, deleteTopic, toggleDiscussed, canEdit: canEditTopic, loading: topicsLoading } = useTopics(currentSession?.id, selectedRelationship?.participants);
  const { actionItems, getCarryoverItems, addActionItem, updateActionItem, deleteActionItem, toggleCompleted, canEdit: canEditActionItem, isOverdue } = useActionItems(currentSession?.id, relationshipSessionIds, selectedRelationship?.participants);
  const { incompleteGoals, toggleCompleted: toggleGoalCompleted, isOverdue: isGoalOverdue } = useGoals();

  // Private notes for this session
  const { notes, loading: notesLoading, saving: notesSaving, saveNotes } = useOneOnOneNotes(currentSession?.id, selectedRelationshipId);
  const [notesContent, setNotesContent] = useState('');
  const [notesInitialized, setNotesInitialized] = useState(false);

  // Sync notes content when loading completes
  useEffect(() => {
    if (!notesLoading && notes) {
      setNotesContent(notes.content || '');
      setNotesInitialized(true);
    }
  }, [notesLoading, notes]);

  // Reset notes when session changes
  useEffect(() => {
    setNotesContent('');
    setNotesInitialized(false);
  }, [currentSession?.id]);

  // Auto-save notes with debounce
  const handleNotesChange = useCallback((e) => {
    setNotesContent(e.target.value);
  }, []);

  const handleNotesSave = useCallback(() => {
    if (notesInitialized && notesContent !== (notes?.content || '')) {
      saveNotes(notesContent);
    }
  }, [notesContent, notes?.content, notesInitialized, saveNotes]);

  // Past session selection
  const [selectedPastSession, setSelectedPastSession] = useState(null);
  const [showPastDropdown, setShowPastDropdown] = useState(false);

  // Past session data
  const { topics: pastTopics } = useTopics(selectedPastSession?.id, selectedRelationship?.participants);
  const { actionItems: pastActionItems } = useActionItems(selectedPastSession?.id, relationshipSessionIds, selectedRelationship?.participants);

  // Modals
  const [showTopicModal, setShowTopicModal] = useState(false);
  const [showActionItemModal, setShowActionItemModal] = useState(false);
  const [showRelationshipModal, setShowRelationshipModal] = useState(false);
  const [editingTopic, setEditingTopic] = useState(null);
  const [editingActionItem, setEditingActionItem] = useState(null);
  const [editingRelationship, setEditingRelationship] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState({ show: false, type: null, item: null });

  // Reset past session when changing relationships
  useEffect(() => {
    setSelectedPastSession(null);
  }, [selectedRelationshipId]);

  const carryoverItems = useMemo(() => {
    if (!currentSession) return [];
    return getCarryoverItems(currentSession.id);
  }, [currentSession, getCarryoverItems]);

  // Topic handlers
  const handleAddTopic = () => {
    setEditingTopic(null);
    setShowTopicModal(true);
  };

  const handleEditTopic = (topic) => {
    setEditingTopic(topic);
    setShowTopicModal(true);
  };

  const handleSaveTopic = async (data) => {
    if (editingTopic) {
      await updateTopic(editingTopic.id, data);
    } else {
      await addTopic(data);
    }
  };

  // Action item handlers
  const handleAddActionItem = () => {
    setEditingActionItem(null);
    setShowActionItemModal(true);
  };

  const handleEditActionItem = (item) => {
    setEditingActionItem(item);
    setShowActionItemModal(true);
  };

  const handleSaveActionItem = async (data) => {
    if (editingActionItem) {
      await updateActionItem(editingActionItem.id, data);
    } else {
      await addActionItem(data);
    }
  };

  // Relationship handlers
  const handleAddRelationship = () => {
    setEditingRelationship(null);
    setShowRelationshipModal(true);
  };

  const handleEditRelationship = (relationship) => {
    setEditingRelationship(relationship);
    setShowRelationshipModal(true);
  };

  const handleSaveRelationship = async (data) => {
    if (editingRelationship) {
      await updateRelationship(editingRelationship.id, data);
    } else {
      const newId = await addRelationship(data.personName, data.meetingDay, currentUserName);
      setSelectedRelationshipId(newId);
    }
  };

  const handleArchiveRelationship = async (relationship) => {
    await archiveRelationship(relationship.id);
    if (selectedRelationshipId === relationship.id) {
      setSelectedRelationshipId(activeRelationships.find(r => r.id !== relationship.id)?.id || null);
    }
  };

  const handleRestoreRelationship = async (relationship) => {
    await restoreRelationship(relationship.id);
  };

  // Delete confirmation
  const handleDeleteConfirm = () => {
    if (deleteConfirm.type === 'topic') {
      deleteTopic(deleteConfirm.item.id);
    } else if (deleteConfirm.type === 'actionItem') {
      deleteActionItem(deleteConfirm.item.id);
    }
    setDeleteConfirm({ show: false, type: null, item: null });
  };

  const loading = relationshipsLoading || sessionsLoading || topicsLoading;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-text-tertiary">Loading...</div>
      </div>
    );
  }

  // No relationships yet
  if (activeRelationships.length === 0 && archivedRelationships.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 gap-4">
        <Users size={48} className="text-text-tertiary" />
        <div className="text-center">
          <h3 className="text-lg font-semibold text-text-primary mb-1">No 1:1 Relationships</h3>
          <p className="text-sm text-text-tertiary mb-4">Add someone to start tracking your 1:1 meetings</p>
          <button
            onClick={handleAddRelationship}
            className="inline-flex items-center gap-2 px-4 py-2 bg-accent text-white rounded-lg text-sm font-medium cursor-pointer border-none hover:bg-accent-hover transition-all"
          >
            <Plus size={16} />
            Add Person
          </button>
        </div>

        <AddRelationshipModal
          isOpen={showRelationshipModal}
          onClose={() => {
            setShowRelationshipModal(false);
            setEditingRelationship(null);
          }}
          onSave={handleSaveRelationship}
          editItem={editingRelationship}
        />
      </div>
    );
  }

  const isViewingPast = !!selectedPastSession;
  const viewSession = selectedPastSession || currentSession;
  const viewTopics = selectedPastSession ? pastTopics : topics;
  const viewActionItems = selectedPastSession ? pastActionItems : actionItems;
  const isEditable = isAdmin && isSessionEditable(viewSession);
  const isCompleted = viewSession?.status === 'completed';

  const handleToggleComplete = async () => {
    if (!viewSession) return;
    if (isCompleted) {
      await uncompleteSession(viewSession.id);
    } else {
      // Complete current session - this auto-creates the next one
      await completeSession(viewSession.id);
    }
  };

  const handleStartSession = async () => {
    if (isCreatingSession) return;
    setIsCreatingSession(true);
    try {
      await createFirstSession();
    } finally {
      setIsCreatingSession(false);
    }
  };

  const displayedRelationships = showArchived
    ? [...activeRelationships, ...archivedRelationships]
    : activeRelationships;

  return (
    <FeatureOnboarding feature="oneOnOnes">
    <div>
      {/* Relationship Cards */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-medium text-text-secondary">Your 1:1s</h3>
          <div className="flex items-center gap-2">
            {archivedRelationships.length > 0 && (
              <button
                onClick={() => setShowArchived(!showArchived)}
                className="flex items-center gap-1 text-[11px] text-text-tertiary hover:text-text-secondary cursor-pointer bg-transparent border-none transition-all"
              >
                <Archive size={12} />
                {showArchived ? 'Hide Archived' : `Show Archived (${archivedRelationships.length})`}
              </button>
            )}
            <button
              onClick={handleAddRelationship}
              className="flex items-center gap-1 px-2 py-1 bg-surface-hover text-text-secondary rounded-md text-xs font-medium cursor-pointer border-none hover:bg-surface-active hover:text-text-primary transition-all"
            >
              <Plus size={14} />
              Add
            </button>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
          {displayedRelationships.map((relationship) => (
            <RelationshipCard
              key={relationship.id}
              relationship={relationship}
              isSelected={selectedRelationshipId === relationship.id}
              onClick={() => setSelectedRelationshipId(relationship.id)}
              onEdit={handleEditRelationship}
              onArchive={handleArchiveRelationship}
              onRestore={handleRestoreRelationship}
              topicCount={relationship.id === selectedRelationshipId ? topics.filter(t => !t.discussed).length : 0}
              actionItemCount={relationship.id === selectedRelationshipId ? actionItems.filter(a => !a.completed).length : 0}
            />
          ))}
        </div>
      </div>

      {/* Divider */}
      <div className="h-px bg-border mb-4" />

      {/* Session Header */}
      {selectedRelationship && (
        <>
          <div className="flex flex-col gap-3 mb-4 md:flex-row md:items-center md:justify-between">
            {/* Title and badges */}
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="text-lg font-semibold text-text-primary">
                1:1 with {selectedRelationship.personName}
              </h2>
              <span className="px-2 py-0.5 rounded bg-surface-hover text-[10px] text-text-tertiary">
                {DAY_NAMES[selectedRelationship.meetingDay]}s
              </span>
              {viewSession && (
                <span className="text-sm text-text-tertiary">
                  Started {formatDate(viewSession.weekStart)}
                </span>
              )}
              {isCompleted && (
                <span className="px-2.5 py-1 rounded-lg text-xs font-semibold bg-success-light text-success flex items-center gap-1.5">
                  <CheckCircle size={12} />
                  Complete
                </span>
              )}
              {isViewingPast && !isCompleted && (
                <span className="px-2.5 py-1 rounded-lg text-xs font-semibold bg-red-500/15 text-red-400 flex items-center gap-1.5">
                  <Circle size={12} />
                  Not Held
                </span>
              )}
            </div>

            {/* Actions */}
            <div className="flex items-center gap-2">
              {/* Mark Complete Button - Admin only */}
              {isAdmin && viewSession && (
                <button
                  onClick={handleToggleComplete}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium cursor-pointer border-none transition-all ${
                    isCompleted
                      ? 'bg-surface-hover text-text-secondary hover:bg-surface-active'
                      : 'bg-success text-white hover:bg-success/90 shadow-lg shadow-success/20'
                  }`}
                >
                  {isCompleted ? (
                    <>
                      <Circle size={16} />
                      Reopen
                    </>
                  ) : (
                    <>
                      <CheckCircle size={16} />
                      Complete & Start Next
                    </>
                  )}
                </button>
              )}

              {/* Session History Dropdown */}
              <div className="relative">
                <button
                  onClick={() => setShowPastDropdown(!showPastDropdown)}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-surface-card border border-border rounded-lg text-xs text-text-secondary hover:bg-surface-hover hover:text-text-primary cursor-pointer transition-all"
                >
                  {isViewingPast ? 'Viewing Past' : 'Past Sessions'}
                  <ChevronDown size={16} />
                </button>

                {showPastDropdown && (
                  <div className="absolute right-0 top-full mt-1 bg-surface-card border border-border rounded-lg shadow-lg z-10 min-w-[220px] overflow-hidden">
                    <button
                      onClick={() => {
                        setSelectedPastSession(null);
                        setShowPastDropdown(false);
                      }}
                      className={`w-full text-left px-3 py-2 text-xs hover:bg-surface-hover cursor-pointer border-none bg-transparent transition-all ${!isViewingPast ? 'font-medium text-accent' : 'text-text-secondary'}`}
                    >
                      Current Session
                    </button>
                    {pastSessions.map((session) => (
                      <button
                        key={session.id}
                        onClick={() => {
                          setSelectedPastSession(session);
                          setShowPastDropdown(false);
                        }}
                        className={`w-full text-left px-3 py-2 text-xs hover:bg-surface-hover cursor-pointer border-none bg-transparent flex items-center justify-between transition-all ${selectedPastSession?.id === session.id ? 'font-medium text-accent' : 'text-text-secondary'}`}
                      >
                        <span>Started {formatDate(session.weekStart)}</span>
                        {session.status === 'completed' ? (
                          <CheckCircle size={12} className="text-success" />
                        ) : (
                          <Circle size={12} className="text-red-400" />
                        )}
                      </button>
                    ))}
                    {pastSessions.length === 0 && (
                      <div className="px-3 py-2 text-xs text-text-tertiary italic">
                        No past sessions yet
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Status Banner */}
          {isViewingPast && isEditable && (
            <div className="bg-accent-muted border border-accent/30 rounded-lg p-2.5 mb-4 text-xs text-text-secondary">
              This 1:1 was not marked complete. You can still edit it. <button onClick={() => setSelectedPastSession(null)} className="text-accent font-medium underline cursor-pointer bg-transparent border-none">Return to current session</button>
            </div>
          )}
          {isViewingPast && !isEditable && (
            <div className="bg-surface-card border border-border rounded-lg p-2.5 mb-4 text-xs text-text-tertiary">
              Viewing completed 1:1 (read-only). <button onClick={() => setSelectedPastSession(null)} className="text-accent font-medium underline cursor-pointer bg-transparent border-none">Return to current session</button>
            </div>
          )}

          {/* No Active Session - Start New Session */}
          {!currentSession && !isViewingPast && (
            <div className="flex flex-col items-center justify-center py-12 gap-4 bg-surface-card rounded-xl border border-border">
              <MessageSquare size={48} className="text-text-tertiary" />
              <div className="text-center">
                <h3 className="text-lg font-semibold text-text-primary mb-1">
                  {sessions.length === 0 ? 'Start Your First 1:1' : 'Start New Session'}
                </h3>
                <p className="text-sm text-text-tertiary mb-4">
                  {sessions.length === 0
                    ? `Begin tracking your 1:1 meetings with ${selectedRelationship.personName}`
                    : 'Your previous session was completed. Start a new one to continue.'}
                </p>
                <button
                  onClick={handleStartSession}
                  disabled={isCreatingSession}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-accent text-white rounded-lg text-sm font-medium cursor-pointer border-none hover:bg-accent-hover transition-all disabled:opacity-50"
                >
                  <Plus size={16} />
                  {isCreatingSession ? 'Starting...' : 'Start Session'}
                </button>
              </div>
              {/* Show carryover items even when no active session */}
              {carryoverItems.length > 0 && (
                <div className="mt-4 w-full max-w-md px-4">
                  <p className="text-xs text-text-tertiary mb-2 text-center">
                    {carryoverItems.length} incomplete action item{carryoverItems.length !== 1 ? 's' : ''} from previous sessions
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Main Grid - 2 Columns - Only show when there's a session */}
          {viewSession && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Column 1: Topics + Carryover Items */}
            <div className="flex flex-col gap-4">
              {/* Topics to Discuss */}
              <Section
                title="Topics to Discuss"
                icon={MessageSquare}
                iconColor="text-weekly"
                count={viewTopics.length}
                onAdd={isEditable ? handleAddTopic : undefined}
              >
                {viewTopics.map((topic) => (
                  <div
                    key={topic.id}
                    className={`flex items-start gap-2 p-2.5 bg-surface-card rounded-lg border border-border mb-1.5 transition-all ${topic.discussed ? 'opacity-60' : ''}`}
                  >
                    {isEditable && (
                      <Checkbox
                        checked={topic.discussed}
                        onChange={() => toggleDiscussed(topic.id, topic.discussed)}
                      />
                    )}
                    {!isEditable && (
                      <div className={`w-4 h-4 rounded border ${topic.discussed ? 'bg-accent border-accent' : 'border-border'} flex items-center justify-center`}>
                        {topic.discussed && <span className="text-surface-base text-[10px]">✓</span>}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className={`text-xs text-text-primary ${topic.discussed ? 'line-through' : ''}`}>
                        {topic.text}
                      </p>
                      <span className="text-[10px] text-text-tertiary">Added by {topic.addedBy}</span>
                    </div>
                    {isEditable && canEditTopic(topic) && (
                      <div className="flex gap-0.5">
                        <button
                          onClick={() => handleEditTopic(topic)}
                          className="p-1.5 rounded-md bg-transparent border-none text-text-tertiary cursor-pointer hover:bg-surface-hover hover:text-text-primary transition-all"
                        >
                          <Edit2 size={12} />
                        </button>
                        <button
                          onClick={() => setDeleteConfirm({ show: true, type: 'topic', item: topic })}
                          className="p-1.5 rounded-md bg-transparent border-none text-text-tertiary cursor-pointer hover:bg-red-500/10 hover:text-red-400 transition-all"
                        >
                          <Trash2 size={12} />
                        </button>
                      </div>
                    )}
                  </div>
                ))}
                {viewTopics.length === 0 && (
                  <p className="text-text-tertiary text-sm italic">
                    No topics yet. Add something to discuss!
                  </p>
                )}
              </Section>

              {/* Carryover Items - Action Items only */}
              {!isViewingPast && carryoverItems.length > 0 && (
                <Section
                  title="Carryover Items"
                  icon={AlertTriangle}
                  iconColor="text-red-400"
                  count={carryoverItems.length}
                  defaultOpen={true}
                >
                  <p className="text-[10px] text-text-tertiary mb-2">
                    Incomplete action items from previous 1:1s
                  </p>

                  {carryoverItems.map((item) => (
                    <div
                      key={item.id}
                      className={`flex items-start gap-2 p-2.5 bg-surface-card rounded-lg border mb-1.5 ${isOverdue(item.dueDate) ? 'border-red-500/30' : 'border-border'}`}
                    >
                      <Checkbox
                        checked={item.completed}
                        onChange={() => toggleCompleted(item.id, item.completed)}
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-text-primary">{item.text}</p>
                        <div className="flex items-center gap-1.5 mt-0.5">
                          <span className="text-[10px] text-text-tertiary">{item.assignedTo}</span>
                          <span className="text-[10px] text-text-muted">•</span>
                          <span className={`text-[10px] ${isOverdue(item.dueDate) ? 'text-red-400 font-medium' : 'text-text-tertiary'}`}>
                            {isOverdue(item.dueDate) ? 'OVERDUE - ' : ''}
                            Due {formatDate(item.dueDate)}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </Section>
              )}

              {/* My Notes - Private per user */}
              {!isViewingPast && currentSession && (
                <Section
                  title="My Notes"
                  icon={FileText}
                  iconColor="text-text-tertiary"
                  defaultOpen={false}
                >
                  <p className="text-[10px] text-text-tertiary mb-2">
                    Private notes only you can see
                  </p>
                  <div className="relative">
                    <textarea
                      value={notesContent}
                      onChange={handleNotesChange}
                      onBlur={handleNotesSave}
                      placeholder="Add your private notes here..."
                      className="w-full min-h-[120px] p-3 bg-surface-card border border-border rounded-lg text-xs text-text-primary resize-y outline-none focus:border-accent transition-colors"
                    />
                    {notesSaving && (
                      <span className="absolute bottom-2 right-2 text-[10px] text-text-tertiary">
                        Saving...
                      </span>
                    )}
                  </div>
                </Section>
              )}
            </div>

            {/* Column 2: Action Items + Goals */}
            <div className="flex flex-col gap-4">
              {/* Action Items */}
              <Section
                title="Action Items"
                icon={CheckSquare}
                iconColor="text-accent"
                count={viewActionItems.length}
                onAdd={isEditable ? handleAddActionItem : undefined}
              >
                {viewActionItems.map((item) => (
                  <div
                    key={item.id}
                    className={`flex items-start gap-2 p-2.5 bg-surface-card rounded-lg border border-border mb-1.5 transition-all ${item.completed ? 'opacity-60' : ''}`}
                  >
                    {isEditable && (
                      <Checkbox
                        checked={item.completed}
                        onChange={() => toggleCompleted(item.id, item.completed)}
                      />
                    )}
                    {!isEditable && (
                      <div className={`w-4 h-4 rounded border ${item.completed ? 'bg-accent border-accent' : 'border-border'} flex items-center justify-center`}>
                        {item.completed && <span className="text-surface-base text-[10px]">✓</span>}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className={`text-xs text-text-primary ${item.completed ? 'line-through' : ''}`}>
                        {item.text}
                      </p>
                      <div className="flex items-center gap-1.5 mt-0.5">
                        <span className="text-[10px] text-text-tertiary">{item.assignedTo}</span>
                        <span className="text-[10px] text-text-muted">•</span>
                        <span className={`text-[10px] ${isOverdue(item.dueDate) && !item.completed ? 'text-red-400 font-medium' : 'text-text-tertiary'}`}>
                          {isOverdue(item.dueDate) && !item.completed ? 'OVERDUE - ' : ''}
                          Due {formatDate(item.dueDate)}
                        </span>
                      </div>
                    </div>
                    {isEditable && canEditActionItem(item) && (
                      <div className="flex gap-0.5">
                        <button
                          onClick={() => handleEditActionItem(item)}
                          className="p-1.5 rounded-md bg-transparent border-none text-text-tertiary cursor-pointer hover:bg-surface-hover hover:text-text-primary transition-all"
                        >
                          <Edit2 size={12} />
                        </button>
                        <button
                          onClick={() => setDeleteConfirm({ show: true, type: 'actionItem', item })}
                          className="p-1.5 rounded-md bg-transparent border-none text-text-tertiary cursor-pointer hover:bg-red-500/10 hover:text-red-400 transition-all"
                        >
                          <Trash2 size={12} />
                        </button>
                      </div>
                    )}
                  </div>
                ))}
                {viewActionItems.length === 0 && (
                  <p className="text-text-tertiary text-sm italic">
                    No action items yet.
                  </p>
                )}
              </Section>

              {/* Goals Review - Only show on current week view and if enabled for this relationship */}
              {!isViewingPast && incompleteGoals.length > 0 && selectedRelationship?.showGoals !== false && (
                <Section
                  title="Goals"
                  icon={Target}
                  iconColor="text-monthly"
                  count={incompleteGoals.length}
                  defaultOpen={true}
                >
                  <p className="text-[10px] text-text-tertiary mb-2">
                    Review goals from Goals tab
                  </p>

                  {incompleteGoals.map((goal) => (
                    <div
                      key={goal.id}
                      className={`flex items-start gap-2 p-2.5 bg-surface-card rounded-lg border mb-1.5 ${isGoalOverdue(goal.dueDate) ? 'border-red-500/30' : 'border-border'}`}
                    >
                      <Checkbox
                        checked={goal.completed}
                        onChange={() => toggleGoalCompleted(goal.id, goal.completed)}
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-text-primary font-medium">{goal.title}</p>
                        {goal.description && (
                          <p className="text-[10px] text-text-tertiary mt-0.5">{goal.description}</p>
                        )}
                        <div className="flex items-center gap-1.5 mt-0.5">
                          <span className="text-[10px] text-text-tertiary">{goal.owner}</span>
                          <span className="text-[10px] text-text-muted">•</span>
                          <span className={`text-[10px] ${isGoalOverdue(goal.dueDate) ? 'text-red-400 font-medium' : 'text-text-tertiary'}`}>
                            {isGoalOverdue(goal.dueDate) ? 'OVERDUE - ' : ''}
                            Due {formatDate(goal.dueDate)}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </Section>
              )}
            </div>
          </div>
          )}
        </>
      )}

      {/* No relationship selected */}
      {!selectedRelationship && activeRelationships.length > 0 && (
        <div className="flex flex-col items-center justify-center py-8 text-center">
          <p className="text-text-tertiary text-sm">Select a 1:1 relationship above to view details</p>
        </div>
      )}

      {/* Modals */}
      <TopicModal
        isOpen={showTopicModal}
        onClose={() => {
          setShowTopicModal(false);
          setEditingTopic(null);
        }}
        onSave={handleSaveTopic}
        editItem={editingTopic}
        currentUserName={currentUserName}
      />

      <ActionItemModal
        isOpen={showActionItemModal}
        onClose={() => {
          setShowActionItemModal(false);
          setEditingActionItem(null);
        }}
        onSave={handleSaveActionItem}
        editItem={editingActionItem}
        relationshipPersonName={selectedRelationship?.personName}
      />

      <AddRelationshipModal
        isOpen={showRelationshipModal}
        onClose={() => {
          setShowRelationshipModal(false);
          setEditingRelationship(null);
        }}
        onSave={handleSaveRelationship}
        editItem={editingRelationship}
      />

      <ConfirmModal
        isOpen={deleteConfirm.show}
        onClose={() => setDeleteConfirm({ show: false, type: null, item: null })}
        onConfirm={handleDeleteConfirm}
        title={`Delete ${deleteConfirm.type === 'topic' ? 'Topic' : 'Action Item'}`}
        message={`Are you sure you want to delete this ${deleteConfirm.type === 'topic' ? 'topic' : 'action item'}? This action cannot be undone.`}
      />
    </div>
    </FeatureOnboarding>
  );
}
